import {FC, useState} from 'react'
import {UserGroupCard} from '../user-group-card/UserGroupCard'
import {UserGroup} from '@/features/user-groups/types'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {UserGroupModal} from '../user-group-modal/UserGroupModal'
import {StyledGridContainer, StyledResponsiveGrid} from './style'
import {UserGroupsGridSkeleton} from './UserGroupsGridSkeleton'
import {usePermissions} from '@/features/auth/store/store'

interface UserGroupsGridProps {
    groups: UserGroup[]
    onDeleteGroup: (group: UserGroup) => void
    isLoading?: boolean
}

export const UserGroupsGrid: FC<UserGroupsGridProps> = ({groups, onDeleteGroup, isLoading = false}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [editingGroup, setEditingGroup] = useState<UserGroup | undefined>(undefined)

    const handleAddClick = () => {
        setEditingGroup(undefined)
        setIsCreateModalOpen(true)
    }

    const handleEditClick = (group: UserGroup) => {
        setEditingGroup(group)
        setIsCreateModalOpen(true)
    }

    const handleModalClose = () => {
        setIsCreateModalOpen(false)
        setEditingGroup(undefined)
    }

    if (isLoading) {
        return <UserGroupsGridSkeleton />
    }

    if (groups.length === 0) {
        return (
            <div className="empty-state-container">
                <EmptyState title={t('user_groups:empty_state_title')} subtitle={t('user_groups:empty_state_subtitle')}>
                    {permissions?.users?.write && (
                        <Button variant="primaryNeutral" onClick={handleAddClick} type="button">
                            <PlusIcon />
                            {t('user_groups:add_group')}
                        </Button>
                    )}
                </EmptyState>

                {isCreateModalOpen && <UserGroupModal onClose={handleModalClose} initialGroup={editingGroup} />}
            </div>
        )
    }

    return (
        <>
            <StyledGridContainer>
                <StyledResponsiveGrid columns={3} gap={4}>
                    {groups.map(group => (
                        <UserGroupCard key={group.id} group={group} onEdit={handleEditClick} onDelete={onDeleteGroup} />
                    ))}
                </StyledResponsiveGrid>
            </StyledGridContainer>

            {isCreateModalOpen && <UserGroupModal onClose={handleModalClose} initialGroup={editingGroup} />}
        </>
    )
}
